import { Component } from '@theme/component';

class ProPlayInspector extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }
  updatedCallback() {
    super.updatedCallback();
    this.initialize();
  }
  initialize() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.frames = [...this.querySelectorAll('[data-frame]')];
    this.range = this.querySelector('[data-range]');
    this.index = 0;
    if (!this.frames.length || !this.range) return;
    this.querySelector('[data-controls]').hidden = false;
    this.range.addEventListener('input', () => this.show(Number(this.range.value) - 1), { signal });
    this.querySelector('[data-previous]').addEventListener('click', () => this.show(this.index - 1), { signal });
    this.querySelector('[data-next]').addEventListener('click', () => this.show(this.index + 1), { signal });
    const stage = this.querySelector('[data-stage]');
    if (this.dataset.spin === 'true') {
      stage.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.button !== 0) return;
        this.drag = { id:event.pointerId, x:event.clientX, index:this.index };
        stage.setPointerCapture(event.pointerId);
      }, { signal });
      stage.addEventListener('pointermove', event => {
        if (this.drag?.id !== event.pointerId) return;
        this.show(this.drag.index + Math.trunc((event.clientX - this.drag.x) / 14));
      }, { signal });
      const release = () => { this.drag = null; };
      stage.addEventListener('pointerup', release, { signal });
      stage.addEventListener('pointercancel', release, { signal });
      stage.addEventListener('lostpointercapture', release, { signal });
    }
    this.show(0);
  }
  show(index) {
    if (!this.frames?.length) return;
    const count = this.frames.length;
    this.index = ((index % count) + count) % count;
    this.frames.forEach((frame, number) => { frame.hidden = number !== this.index; });
    const image = this.frames[this.index].querySelector('img');
    if (image) image.loading = 'eager';
    this.range.value = String(this.index + 1);
    const status = this.dataset.status.replace('__NUMBER__', String(this.index + 1));
    this.range.setAttribute('aria-valuetext', status);
    this.querySelector('[data-status-text]').textContent = status;
    this.querySelectorAll('[data-previous],[data-next]').forEach(button => { button.disabled = count < 2; });
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();
    this.drag = null;
  }
}

class ProPlayTeamPortal extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }
  updatedCallback() {
    super.updatedCallback();
    this.initialize();
  }
  initialize() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.querySelector('[data-add-row]').hidden = false;
    this.querySelector('[data-add-row]').addEventListener('click', () => {
      const rows = this.querySelector('[data-roster-rows]');
      if (rows.children.length >= 8) return;
      rows.append(this.querySelector('template').content.cloneNode(true));
      this.numberRows();
      rows.lastElementChild.querySelector('input').focus();
      this.update();
    }, { signal });
    this.addEventListener('click', event => {
      const button = event.target.closest('[data-remove-row]');
      if (!button || this.querySelectorAll('[data-roster-row]').length < 2) return;
      button.closest('[data-roster-row]').remove();
      this.numberRows();
      this.querySelector('[data-add-row]').focus();
      this.update();
    }, { signal });
    this.form.addEventListener('input', () => this.update(), { signal });
    this.form.addEventListener('change', () => this.update(), { signal });
    this.form.addEventListener('submit', event => {
      this.update();
      if (!this.form.checkValidity()) {
        event.preventDefault();
        this.form.reportValidity();
      }
    }, { signal });
    this.numberRows();
    this.update();
  }
  numberRows() {
    const rows = [...this.querySelectorAll('[data-roster-row]')];
    rows.forEach((row, index) => {
      row.querySelector('[data-row-number]').textContent = String(index + 1).padStart(2,'0');
      row.querySelectorAll('[data-row-field]').forEach(field => {
        field.id = `${this.id}-row-${index + 1}-${field.dataset.rowField}`;
        field.name = `contact[Roster ${index + 1} ${field.dataset.rowField}]`;
        row.querySelector(`[data-row-label="${field.dataset.rowField}"]`).htmlFor = field.id;
      });
      const remove = row.querySelector('[data-remove-row]');
      remove.hidden = rows.length < 2;
      remove.setAttribute('aria-label', this.dataset.removeLabel.replace('__NUMBER__', String(index + 1)));
    });
    this.querySelector('[data-add-row]').disabled = rows.length >= 8;
  }
  update() {
    const team = this.querySelector('[data-team-name]').value.trim();
    this.querySelector('[data-preview-name]').textContent = team || this.dataset.defaultTeam;
    for (const color of ['primary','secondary']) {
      const value = this.querySelector(`[data-color="${color}"]`).value;
      if (/^#[0-9a-f]{6}$/i.test(value)) this.querySelector('[data-identity]').style.setProperty(`--team-${color}`, value);
      this.querySelector(`[data-color-value="${color}"]`).textContent = value.toUpperCase();
    }
    const total = this.querySelector('[data-total]');
    const count = [...this.querySelectorAll('[data-row-field="Quantity"]')].reduce((sum, field) => sum + (Number(field.value) || 0), 0);
    const expected = Number(total.value) || 0;
    total.setCustomValidity(count > 0 && expected > 0 && count !== expected ? this.dataset.quantityError : '');
    this.querySelector('[data-roster-status]').textContent = this.dataset.rosterStatus.replace('__COUNT__',String(count)).replace('__TOTAL__',String(expected));
    for (const row of this.querySelectorAll('[data-roster-row]')) {
      const size = row.querySelector('[data-row-field="Size"]');
      const quantity = row.querySelector('[data-row-field="Quantity"]');
      quantity.setCustomValidity(size.value.trim() && !quantity.value ? this.dataset.rowQuantityError : '');
      size.setCustomValidity(quantity.value && !size.value.trim() ? this.dataset.rowSizeError : '');
    }
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();
  }
}

if (!customElements.get('proplay-inspector')) customElements.define('proplay-inspector', ProPlayInspector);
if (!customElements.get('proplay-team-portal')) customElements.define('proplay-team-portal', ProPlayTeamPortal);
